import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import SortableList from "../../performance/components/builder/SortableList";
import { dataExchangeApi, saveFile } from "../../../api/adminToolsApi";
import {
  useCreateDataTemplate,
  useDataTemplates,
  useDatasets,
  useDeleteDataTemplate,
} from "../../../api/queries/adminToolsQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { FilterField, TemplatePicks } from "./DataToolBits";
import "../admin.css";

const EMPTY = [];

/** Columns for a dataset: default columns first (ticked), the rest after (unticked). */
const initialColumns = (dataset) => {
  const fields = (dataset?.fields || []).filter((f) => f.exportable !== false);
  const on = fields.filter((f) => f.defaultColumn);
  const off = fields.filter((f) => !f.defaultColumn);
  return [...on, ...off].map((f) => ({
    _id: f.key,
    field: f.key,
    label: f.label,
    header: f.label,
    on: !!f.defaultColumn,
  }));
};

/**
 * Export builder: dataset → columns (tick, drag to reorder, rename headers) →
 * filters → format → download. Max 50,000 rows. Settings can be saved as a template.
 */
const ExportPage = () => {
  const datasetsQuery = useDatasets();
  const datasets = (datasetsQuery.data || EMPTY).filter((d) => d.canExport);
  const [datasetKey, setDatasetKey] = useState("");
  const dataset = datasets.find((d) => d.key === datasetKey) || null;
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [format, setFormat] = useState("xlsx");
  const [fileName, setFileName] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);

  const templates = useDataTemplates(
    { dataset: datasetKey, kind: "EXPORT" },
    { enabled: !!datasetKey },
  );
  const createTemplate = useCreateDataTemplate();
  const deleteTemplate = useDeleteDataTemplate();

  useEffect(() => {
    if (!datasetKey && datasets.length) setDatasetKey(datasets[0].key);
  }, [datasetKey, datasets]);
  useEffect(() => {
    setColumns(initialColumns(dataset));
    setFilters({});
    setTemplateId(null);
  }, [dataset]);

  const chosen = columns.filter((c) => c.on);
  const setCol = (field, changes) =>
    setColumns((list) =>
      list.map((c) => (c.field === field ? { ...c, ...changes } : c)),
    );
  const fieldLabel = useMemo(
    () => new Map((dataset?.fields || []).map((f) => [f.key, f.label])),
    [dataset],
  );

  const applyTemplate = (t) => {
    const byField = new Map(
      (t.columns || []).map((c, i) => [
        c.field,
        { header: c.header, order: i },
      ]),
    );
    setColumns((list) => {
      const picked = list
        .filter((c) => byField.has(c.field))
        .sort(
          (a, b) => byField.get(a.field).order - byField.get(b.field).order,
        );
      const rest = list.filter((c) => !byField.has(c.field));
      return [
        ...picked.map((c) => ({
          ...c,
          on: true,
          header: byField.get(c.field).header || c.label,
        })),
        ...rest.map((c) => ({ ...c, on: false })),
      ];
    });
    setFilters(
      Object.fromEntries(
        Object.entries(t.filters || {}).map(([k, v]) => [k, String(v)]),
      ),
    );
    if (t.format) setFormat(t.format);
    setTemplateId(t.id);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const file = await dataExchangeApi.exportFile({
        dataset: datasetKey,
        columns: chosen.map((c) => ({
          field: c.field,
          header: c.header && c.header !== c.label ? c.header : undefined,
        })),
        filters: Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== ""),
        ),
        format,
        templateId: templateId || undefined,
        fileName: fileName.trim() || undefined,
      });
      saveFile(file);
      toast.success(
        file.rowCount !== null
          ? `Exported ${file.rowCount.toLocaleString()} row(s).`
          : "Export downloaded.",
      );
    } catch (err) {
      // Blob error bodies: read the server message from the blob
      let msg = getApiErrorMessage(err, "Could not export.");
      try {
        const text = await err?.response?.data?.text?.();
        if (text) msg = JSON.parse(text).message || msg;
      } catch {
        /* keep the generic message */
      }
      toast.error(Array.isArray(msg) ? msg.join(" ") : msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-4">
          <Link
            to="/admin"
            className="text-slate-500 fs-080 text-decoration-none"
          >
            <i className="bi bi-arrow-left"></i> Administration
          </Link>
          <h1 className="fw-bold text-dark m-0 page-title">Export</h1>
          <p className="text-muted small mb-0">
            Choose the data, the columns and filters, then download Excel or CSV
            (max 50,000 rows).
          </p>
        </div>

        {datasetsQuery.isError && (
          <div className="alert alert-danger fs-085">
            {getApiErrorMessage(
              datasetsQuery.error,
              "Could not load datasets.",
            )}
          </div>
        )}

        <div className="row g-3">
          <div className="col-lg-7">
            <section className="admin-card mb-3">
              <h2 className="admin-card-title">
                <i className="bi bi-database"></i>Data
              </h2>
              <select
                className="form-select mb-2"
                value={datasetKey}
                onChange={(e) => setDatasetKey(e.target.value)}
                aria-label="Dataset"
              >
                {datasets.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
              {dataset?.description && (
                <p className="fs-080 text-slate-600 mb-2">
                  {dataset.description}
                </p>
              )}
              <TemplatePicks
                templates={templates.data || EMPTY}
                activeId={templateId}
                onPick={applyTemplate}
                onDelete={(t) =>
                  window.confirm(`Delete template "${t.name}"?`) &&
                  deleteTemplate.mutate(t.id, {
                    onSuccess: () => toast.success("Template deleted."),
                    onError: (err) =>
                      toast.error(getApiErrorMessage(err, "Could not delete.")),
                  })
                }
              />
            </section>

            <section className="admin-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="admin-card-title m-0">
                  <i className="bi bi-layout-three-columns"></i>Columns (
                  {chosen.length})
                </h2>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light border"
                    onClick={() =>
                      setColumns((l) => l.map((c) => ({ ...c, on: true })))
                    }
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border"
                    onClick={() => setColumns(initialColumns(dataset))}
                  >
                    Default
                  </button>
                </div>
              </div>
              <p className="fs-075 text-slate-500">
                Tick columns, drag to change the order, and rename a header if
                needed.
              </p>
              <SortableList
                className="dt-columns"
                items={columns}
                onReorder={setColumns}
                renderItem={(c, i, { handle, moveUp, moveDown }) => (
                  <div className={`dt-column ${c.on ? "" : "is-off"}`}>
                    {handle}
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={c.on}
                      onChange={(e) =>
                        setCol(c.field, { on: e.target.checked })
                      }
                      aria-label={`Include ${c.label}`}
                    />
                    <span className="text-truncate" title={c.field}>
                      {fieldLabel.get(c.field) || c.field}
                    </span>
                    <input
                      className="form-control form-control-sm"
                      value={c.header}
                      onChange={(e) =>
                        setCol(c.field, { header: e.target.value })
                      }
                      disabled={!c.on}
                      aria-label={`Header for ${c.label}`}
                    />
                    <div className="row-tools">
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={moveUp}
                        disabled={!moveUp}
                        aria-label="Move up"
                      >
                        <i className="bi bi-arrow-up"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={moveDown}
                        disabled={!moveDown}
                        aria-label="Move down"
                      >
                        <i className="bi bi-arrow-down"></i>
                      </button>
                    </div>
                  </div>
                )}
              />
            </section>
          </div>

          <div className="col-lg-5">
            <section className="admin-card mb-3">
              <h2 className="admin-card-title">
                <i className="bi bi-funnel"></i>Filters
              </h2>
              {(dataset?.filters || []).length === 0 && (
                <p className="fs-080 text-slate-500 mb-0">
                  No filters for this data.
                </p>
              )}
              <div className="d-flex flex-column gap-3">
                {(dataset?.filters || []).map((f) => (
                  <FilterField
                    key={f.key}
                    filter={f}
                    value={filters[f.key]}
                    onChange={(k, v) => setFilters((x) => ({ ...x, [k]: v }))}
                  />
                ))}
              </div>
            </section>

            <section className="admin-card">
              <h2 className="admin-card-title">
                <i className="bi bi-download"></i>Download
              </h2>
              <div className="row g-2 mb-3">
                <div className="col-5">
                  <label htmlFor="export-format" className="profile-label">
                    Format
                  </label>
                  <select
                    id="export-format"
                    className="form-select"
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                  >
                    <option value="xlsx">Excel (.xlsx)</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
                <div className="col-7">
                  <label htmlFor="export-name" className="profile-label">
                    File name{" "}
                    <span className="text-slate-400 fw-normal">(optional)</span>
                  </label>
                  <input
                    id="export-name"
                    className="form-control"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder={dataset?.key || "export"}
                  />
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary profile-btn"
                  onClick={download}
                  disabled={!dataset || !chosen.length || downloading}
                >
                  {downloading && (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    ></span>
                  )}
                  <i className="bi bi-download"></i> Download
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary profile-btn"
                  onClick={() => setSaving(true)}
                  disabled={!dataset || !chosen.length}
                >
                  <i className="bi bi-bookmark-plus"></i> Save as template
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>

      {saving && (
        <SaveTemplateDialog
          busy={createTemplate.isPending}
          onClose={() => setSaving(false)}
          onSave={({ name, description }) =>
            createTemplate.mutate(
              {
                dataset: datasetKey,
                kind: "EXPORT",
                name,
                description: description || undefined,
                columns: chosen.map((c) => ({
                  field: c.field,
                  header: c.header || c.label,
                })),
                filters: Object.fromEntries(
                  Object.entries(filters).filter(([, v]) => v !== ""),
                ),
                format,
              },
              {
                onSuccess: (t) => {
                  toast.success("Template saved.");
                  setTemplateId(t?.id || null);
                  setSaving(false);
                },
                onError: (err) =>
                  toast.error(
                    getApiErrorMessage(err, "Could not save the template."),
                  ),
              },
            )
          }
        />
      )}
    </Layout>
  );
};

/** Name + description for a saved template. */
export const SaveTemplateDialog = ({ onSave, onClose, busy }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  return (
    <Dialog
      title="Save as template"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={() =>
              onSave({ name: name.trim(), description: description.trim() })
            }
            disabled={busy || !name.trim()}
          >
            {busy && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save
          </button>
        </>
      }
    >
      <label htmlFor="tpl-name-input" className="profile-label">
        Name <span className="text-danger">*</span>
      </label>
      <input
        id="tpl-name-input"
        className="form-control mb-3"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        maxLength={100}
      />
      <label htmlFor="tpl-desc-input" className="profile-label">
        Description
      </label>
      <textarea
        id="tpl-desc-input"
        className="form-control"
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </Dialog>
  );
};

export default ExportPage;
